<template>
  <Modal
    v-model="visible"
    :title="$t('Edit Province Risk')"
    :width="700"
    @on-ok="handleSave"
    @on-cancel="handleCancel"
  >
    <div v-if="provinceData">
      <div class="province-header">
        <h3>{{ provinceData.province_name }}</h3>
        <p class="province-code">{{ provinceData.riskString || 'No risk data' }}</p>
      </div>

      <Divider />

      <Form
        ref="form"
        :model="formData"
        label-position="left"
        :label-width="150"
      >
        <FormItem :label="$t('Risk Level')">
          <CountryInfoSlider
            v-model="formData.riskLevel"
            :disabled="!canEdit"
          />
        </FormItem>
      </Form>

      <div v-if="!canEdit" class="permission-notice">
        <Icon type="ios-information-circle" />
        {{ $t('You do not have permission to edit province risks') }}
      </div>
    </div>

    <Spin v-if="loading" fix />
  </Modal>
</template>

<script>
import CountryInfoSlider from './CountryInfoSlider.vue';
import { patchProvinceRiskQry } from '@/services/countryInfo.service';

export default {
  name: "ProvinceRiskModal",
  components: {
    CountryInfoSlider
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    provinceData: {
      type: Object,
      default: null
    },
    country: {
      type: String,
      required: true
    },
    allProvinceRisks: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: {
        riskLevel: 0
      },
      loading: false
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    canEdit() {
      return this.s2ec.user.is_admin || this.s2ec.user.is_superuser;
    }
  },
  watch: {
    provinceData: {
      handler(data) {
        if (data) {
          // Populate form with current risk level
          this.formData.riskLevel = data.riskLevel || 0;
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    async handleSave() {
      if (!this.canEdit) {
        this.$Message.warning(this.$t('You do not have permission to edit'));
        return;
      }

      if (!this.provinceData || !this.provinceData.province_name) {
        this.$Message.error(this.$t('Province data is missing'));
        return;
      }

      this.loading = true;

      try {
        // Update the province risk in the full list
        const updatedProvinceRisks = {
          ...this.allProvinceRisks,
          [this.provinceData.province_name]: `R${this.formData.riskLevel}`
        };

        // PATCH entire country's province list
        await patchProvinceRiskQry(this.country, updatedProvinceRisks);

        this.$Message.success(this.$t('Province risk updated successfully'));
        this.$emit('updated', updatedProvinceRisks);
        this.visible = false;
      } catch (error) {
        console.error('Failed to update province risk:', error);

        if (error.response?.status === 404) {
          this.$Message.error(this.$t('Province risk API endpoint not found'));
        } else if (error.response?.status === 403) {
          this.$Message.error(this.$t('You do not have permission to edit'));
        } else {
          this.$Message.error(this.$t('Failed to update province risk'));
        }
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      // Reset form data to original values
      if (this.provinceData) {
        this.formData.riskLevel = this.provinceData.riskLevel || 0;
      }
    }
  }
};
</script>

<style scoped>
.province-header {
  margin-bottom: 10px;
}

.province-header h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.province-code {
  color: #999;
  font-size: 12px;
  margin: 0;
  font-family: monospace;
}

.permission-notice {
  margin-top: 15px;
  padding: 10px;
  background: #f0f0f0;
  border-radius: 4px;
  color: #666;
  font-size: 13px;
}

.permission-notice .ivu-icon {
  margin-right: 5px;
}
</style>
